import React, { useEffect, useId, useState } from "react";
import styled from "styled-components";
import FormGroup from "./FormGroup";
import FormInputGroup from "./FormInputGroup";
import FormLabel from "./FormLabel";
import InputErrorMessage from "./InputErrorMessage";
import { InputSpinnerMd } from "../loaders/InputSpinners";
import { Form, InputGroup } from "react-bootstrap";
import { NumericFormat } from "react-number-format";

const StyledNumericFormat = styled(NumericFormat)`
    && {
        ${props => props.loading === 'true' ? InputSpinnerMd : ''}
    }

    &[loading="true"] {
        padding-right: 2.5rem !important;
    }
`;

const CurrencyInput = ({
    allowNegative = true,
    className,
    currencySymbol = "£",
    disabled,
    disableAnimations = false,
    decimalScale = 2,
    fixedDecimalScale = true,
    id,
    label,
    onBlur,
    onChange,
    prefixSymbol = true,
    showInputGroup = false,
    successTimeout = 1000,
    thousandSeparator = true,
    value = 0,
    errorMessage = null,
    errorAllowRetry = true,
    ...rest
}) => {
    const defaultComponentId = useId();
    const componentId = id || defaultComponentId;
    const [error, setError] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentValue, setCurrentValue] = useState(value || 0);
    const [originalValue, setOriginalValue] = useState(value || 0);

    const onBlurEvent = (e) => {
        // check if we have a prevent default method we need to stop
        if (e && typeof (e.preventDefault) === 'function') {
            e.preventDefault();
        }

        // check that the value has changed before triggering anything
        // NOTE: also check the function HAS been passed into this component
        if (currentValue === originalValue || !onBlur || typeof (onBlur) !== 'function') {
            return;
        }

        // trigger the loading animation on the input
        if (disableAnimations !== true) {
            setError(_ => null);
            setIsLoading(_ => true);
        }

        // used to safely check the function returns something we can check on
        var possibleResult = onBlur(currentValue, e);

        // we can now safely use the `then` method and handle the response accordingly
        Promise.resolve(possibleResult).then(
            _ => {
                setOriginalValue(currentValue ?? 0);
                if (disableAnimations === true) {
                    return;
                }
                setIsLoading(_ => false);
                setIsSuccess(_ => true);
            },
            error => {
                if (disableAnimations === true) {
                    return;
                }
                setIsLoading(_ => false);
                setError(_ => error);
            }
        );
    };

    const onChangeEvent = (values, sourceInfo) => {
        const { event } = sourceInfo;
        const { floatValue } = values;

        setCurrentValue(floatValue ?? 0);
        if (onChange && typeof (onChange) === 'function') {
            onChange(event, values);
        }
    };

    useEffect(() => {
        if (errorMessage) {
            setError(_ => errorMessage);
        }
        else {
            setError(_ => null);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (isSuccess === false) {
            return;
        }

        let t = setTimeout(() => {
            setIsSuccess(_ => false);
        }, successTimeout);

        return () => clearTimeout(t);
    }, [isSuccess, successTimeout]);

    useEffect(() => {
        if (value !== currentValue) {
            setOriginalValue(value ?? 0);
            setCurrentValue(value ?? 0);
        }
    }, [value]);

    return <FormGroup className={className}>
        {label && typeof label === 'string' && <FormLabel htmlFor={componentId}>{label}</FormLabel>}
        {label && typeof label === 'object' && <React.Fragment>{label}</React.Fragment>}
        <FormInputGroup>
            {showInputGroup === true && (<InputGroup.Text>{currencySymbol}</InputGroup.Text>)}
            <StyledNumericFormat
                id={componentId}
                displayType="input"
                customInput={Form.Control}
                className="text-end"
                value={currentValue}
                loading={isLoading.toString()}
                disabled={disabled || isLoading}
                allowNegative={allowNegative}
                decimalScale={decimalScale}
                fixedDecimalScale={fixedDecimalScale}
                thousandSeparator={thousandSeparator}
                prefix={prefixSymbol === true ? currencySymbol : undefined}
                isInvalid={error !== null}
                isValid={isSuccess}
                onBlur={onBlurEvent}
                onValueChange={onChangeEvent}
                {...rest}
            />
            <InputErrorMessage error={error} retryCallback={onBlurEvent} allowRetry={errorAllowRetry} />
        </FormInputGroup>
    </FormGroup>
};

export default CurrencyInput;